import * as React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Loader2, Play } from 'lucide-react';
import { toolsApi } from '@/api/tools';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { JsonView } from '@/components/data/JsonView';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Field } from '@/components/integrations/forms/shared';
import type { WizardDraft } from './state';
import { coerceTestInputs } from './state';

interface Props {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
  canRun: boolean;
}

interface RunResult {
  ok: boolean;
  result?: unknown;
  error?: string;
}

export function Step5Test({ draft, onChange, canRun }: Props): JSX.Element {
  const { t } = useTranslation();
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState<RunResult | null>(null);

  const updateInput = (name: string, value: string): void => {
    onChange({ testInputs: { ...draft.testInputs, [name]: value } });
  };

  const run = async (): Promise<void> => {
    const coerced = coerceTestInputs(draft);
    if (!coerced.ok) {
      setResult({ ok: false, error: coerced.error });
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await toolsApi.test(draft.toolName, coerced.inputs);
      setResult(res);
    } catch (err) {
      setResult({
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3">
      {!canRun ? (
        <Alert variant="warning">
          <AlertTitle>{t('tools.wizard.step5.noTestTitle')}</AlertTitle>
          <AlertDescription>
            <Trans i18nKey="tools.wizard.step5.noTestBody" components={{ code: <code /> }} />
          </AlertDescription>
        </Alert>
      ) : (
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="tools.wizard.step5.intro"
            values={{ name: draft.toolName }}
            components={{ code: <code /> }}
          />
        </p>
      )}

      {draft.fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('tools.wizard.step5.noFields')}</p>
      ) : (
        <div className="grid gap-3">
          {draft.fields.map((f) => (
            <Field
              key={f.id}
              label={`${f.name} (${f.type})`}
              htmlFor={`test-${f.id}`}
              hint={f.description}
              required={f.required}
            >
              <Input
                id={`test-${f.id}`}
                value={draft.testInputs[f.name] ?? ''}
                onChange={(e) => updateInput(f.name, e.target.value)}
                placeholder={
                  f.type === 'array' ? 'a, b, c' : f.type === 'boolean' ? 'true / false' : ''
                }
                className="font-mono text-xs"
              />
            </Field>
          ))}
        </div>
      )}

      <Button
        type="button"
        onClick={() => void run()}
        disabled={!canRun || running}
        className="gap-1.5"
      >
        {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
        {t('tools.wizard.step5.dryRun')}
      </Button>

      {result ? (
        result.ok ? (
          <Alert variant="success">
            <AlertTitle>{t('tools.wizard.step5.result')}</AlertTitle>
            <AlertDescription>
              <JsonView value={result.result} />
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTitle>{t('tools.wizard.step5.error')}</AlertTitle>
            <AlertDescription>
              <pre className="overflow-auto text-xs">{result.error}</pre>
            </AlertDescription>
          </Alert>
        )
      ) : null}
    </div>
  );
}
